import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import { useLanguageAwareSEO } from "@/hooks/useLanguageAwareSEO";
import { breadcrumbSchema, calculatorToolSchema } from "@/utils/structuredData";

export default function ICETableSolver() {
  useLanguageAwareSEO([
    (lang) => breadcrumbSchema([
      { name: "Home", url: "https://calclab.app" },
      { name: "Chemistry", url: "https://calclab.app/chemistry" },
      { name: "ICE Table Solver", url: "https://calclab.app/calculator/ice-table" }
    ], lang),
    (lang) => calculatorToolSchema(
      "ICE Table Solver",
      "Calculate equilibrium concentrations using Initial, Change, Equilibrium method. Free chemistry calculator.",
      "https://calclab.app/calculator/ice-table",
      lang
    )
  ]);
  const [initialA, setInitialA] = useState("");
  const [initialB, setInitialB] = useState("");
  const [change, setChange] = useState("");
  const [result, setResult] = useState<{ eqA: number; eqB: number; eqC: number } | null>(null);

  const calculate = () => {
    const iA = parseFloat(initialA);
    const iB = parseFloat(initialB);
    const x = parseFloat(change);

    if (isNaN(iA) || isNaN(iB) || isNaN(x)) {
      toast({
        title: "Invalid Input",
        description: "Please enter valid numbers.",
        variant: "destructive",
      });
      return;
    }

    // For A + B ⇌ C
    const eqA = iA - x;
    const eqB = iB - x;
    const eqC = x;

    setResult({ eqA, eqB, eqC });

    toast({
      title: "Calculation Complete",
      description: "ICE table solved successfully",
    });
  };

  const reset = () => {
    setInitialA("");
    setInitialB("");
    setChange("");
    setResult(null);
  };

  return (
    <PageLayout 
      calculatorName="ICE Table Solver" 
      calculatorPath="/calculator/ice-table"
      category="chemistry"
      subcategory="Equilibrium"
    >
      <div className="max-w-2xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle>ICE Table Solver</CardTitle>
            <CardDescription>
              Initial, Change, Equilibrium calculations
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="initialA">Initial [A] (M)</Label>
                <Input
                  id="initialA"
                  type="number"
                  step="any"
                  value={initialA}
                  onChange={(e) => setInitialA(e.target.value)}
                  placeholder="Initial concentration of A"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="initialB">Initial [B] (M)</Label>
                <Input
                  id="initialB"
                  type="number"
                  step="any"
                  value={initialB}
                  onChange={(e) => setInitialB(e.target.value)}
                  placeholder="Initial concentration of B"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="change">Change (x)</Label>
                <Input
                  id="change"
                  type="number"
                  step="any"
                  value={change}
                  onChange={(e) => setChange(e.target.value)}
                  placeholder="Amount reacted"
                />
              </div>
            </div>

            <div className="flex gap-4">
              <Button onClick={calculate} className="flex-1">Calculate</Button>
              <Button onClick={reset} variant="outline" className="flex-1">Reset</Button>
            </div>

            {result && (
              <Card className="bg-muted/50">
                <CardHeader>
                  <CardTitle className="text-lg">Equilibrium Concentrations</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <p>[A] = {result.eqA.toFixed(4)} M</p>
                  <p>[B] = {result.eqB.toFixed(4)} M</p>
                  <p>[C] = {result.eqC.toFixed(4)} M</p>
                </CardContent>
              </Card>
            )}

            <Card className="bg-muted/50">
              <CardHeader>
                <CardTitle className="text-sm">Example: A + B ⇌ C</CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-muted-foreground">
                <p>Initial: [A]₀, [B]₀, [C]₀ = 0</p>
                <p>Change: -x, -x, +x</p>
                <p>Equilibrium: [A]₀-x, [B]₀-x, x</p>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
